import "client-only";
import useSWRMutation from "swr/mutation";
import { fetcher } from "../utils/fetcher";
import { useSWRConfig } from "swr";

/** Wrapped mutation of SWR, see more at https://swr.vercel.app/docs/mutation#useswrmutation */
function useMutation(options, config) {
  const { mutate } = useSWRConfig();
  const { mutateRelatedDataList, ...fetchOpts } = options;
  return useSWRMutation(
    {
      path: fetchOpts.path,
      params: fetchOpts.params,
    },
    (_, { arg }) =>
      fetcher({
        ...fetchOpts,
        ...arg,
      }),
    {
      ...config,
      revalidate: false,
      onSuccess: (_data, _key, _config) => {
        // Update the cache for the current resource
        mutate(
          (key) => typeof key === "object" && key?.path === fetchOpts.path,
          (currentData) => {
            return {
              ...currentData,
              ..._data,
            };
          },
          { revalidate: false },
        );

        if (mutateRelatedDataList) {
          const { mutatePath, insertOnNotFound, shouldRemove, shouldRevalidate, isEqual } = mutateRelatedDataList;

          // Handle removal if necessary
          if (shouldRemove) {
            mutate((key) => typeof key === "object" && key?.path === mutatePath, undefined, { revalidate: false });
            return;
          }

          // Update related data in the cache
          mutate(
            (key) => typeof key === "object" && mutatePath?.includes(key?.path),
            (currentData) => {
              if (!currentData || shouldRevalidate) {
                return currentData;
              }

              // Ensure `currentData.data` is an array
              const dataList = Array.isArray(currentData.data) ? currentData.data : [];

              const index = dataList.findIndex((item) => {
                if (isEqual) {
                  return isEqual(item, _data);
                }
                return item.id === _data.id;
              });

              // Update the data based on the mutation type
              if (shouldRemove && index !== -1) {
                return {
                  ...currentData,
                  total: currentData.total - 1,
                  data: [...dataList.slice(0, index), ...dataList.slice(index + 1)],
                };
              }

              const updatedData = { ...currentData };

              if (index < 0) {
                // Handle insertion if the item is not found
                if (insertOnNotFound) {
                  updatedData.total = (updatedData.total || 0) + 1;
                  if (insertOnNotFound === "start") {
                    updatedData.data = [_data, ...dataList];
                  } else if (insertOnNotFound === "end") {
                    updatedData.data = [...dataList, _data];
                  }
                }
              } else {
                // Update existing item
                updatedData.data[index] = _data;
              }

              return updatedData;
            },
            { revalidate: !!shouldRevalidate },
          );
        }

        // Call user-defined onSuccess if provided
        if (config?.onSuccess) {
          config.onSuccess(_data, _key, _config);
        }
      },
    },
  );
}

export default useMutation;
